import { useEffect, useState } from 'react';

import { errors, isValid, validator } from 'react-validator';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router-dom';

import {
  TeacherGetQuizByIdQuery,
  useGetMyTeachingStructureItemsQuery,
} from '@school_ify/schoolify-repository/src/teacher/types';

import Errors from 'components/Errors/Errors';
import ClassDropDown from 'components/Header/components/ClassDropDown/ClassDropDown';
import Button from 'clean/components/common/Button/Button';
import { CreateEditQuizModel, quizRoutes } from 'types/quiz';
import { ReactComponent as PlusIcon } from 'assets/icons/quiz-plus.svg';
import { ReactComponent as MinusIcon } from 'assets/icons/minus.svg';
import { useAppSelector } from 'redux/hooks';
import { getUser } from 'redux/authentication/auth.slice';

import './CreateQuizForm.scoped.scss';

interface Props {
  editQuiz?: TeacherGetQuizByIdQuery['assignments_by_pk'];
  onSave?: (model: CreateEditQuizModel) => void;
  children?: React.ReactChild;
}

const CreateQuizForm = (props: Props) => {
  const { editQuiz, children, onSave } = props;
  const user = useAppSelector(getUser);
  const { t } = useTranslation();
  const history = useHistory();

  const [validation, setValidation] = useState({});
  const [selectedSubject, setSelectedSubject] = useState({
    title: t('quiz:Subject'),
    id: '',
  });

  const subjectData = useGetMyTeachingStructureItemsQuery({
    variables: {
      _eq: user.id,
    },
  });

  const quizFormAttempts = [0, 1, 2, 3];

  const [model, setModel] = useState<CreateEditQuizModel>({
    id: null,
    timeControl: true,
    description: null,
    title: null,
    duration: 1,
    passPercentage: 1,
    attempts: null,
    due_at: DateTime.now().toISO(),
    teaching_structure_id: null,
  });

  useEffect(() => {
    if (editQuiz?.id && editQuiz.id !== model.id) {
      const fillModel = {
        id: editQuiz.id,
        title: editQuiz.title,
        description: editQuiz.description,
        duration: editQuiz.duration,
        due_at: editQuiz.due_at,
        teaching_structure_id: editQuiz.teaching_structure_id,
        attempts: editQuiz?.assignment_quiz?.attempts,
        passPercentage: editQuiz?.assignment_quiz?.pass_percentage,
        timeControl: editQuiz?.assignment_quiz?.time_control,
      };
      setModel(fillModel);
    }
  }, [editQuiz]);

  useEffect(() => {
    if (subjectData?.data?.teacher_subject && model.teaching_structure_id) {
      const subject = subjectData?.data?.teacher_subject.find(
        subject => subject.teaching_structure.id === model.teaching_structure_id
      );
      if (subject) setSelectedSubject(subject.teaching_structure);
    }
  }, [subjectData?.data?.teacher_subject, model.teaching_structure_id]);

  function setModelProp(e, prop) {
    const copyMod = { ...model };
    copyMod[prop] = e;
    setModel(copyMod);
  }

  async function save() {
    validator(
      { model },
      {
        'model.title': ['required', 'string'],
        'model.description': ['required', 'string'],
        'model.teaching_structure_id': ['required', 'string'],
      },
      {
        'model.title.required': t('study:title_required'),
        'model.description.required': t('study:description_required'),
        'model.teaching_structure_id.required': t('study:structure_id_required'),
      }
    );

    setValidation(errors());
    if (!isValid()) {
      return;
    }
    onSave(model);
  }

  function printSubjects() {
    return subjectData.data ? (
      subjectData.data.teacher_subject.map(subject => {
        return (
          <div
            key={subject.teaching_structure.id}
            className="pointer"
            onClick={() => {
              setSelectedSubject({
                id: subject.teaching_structure.id,
                title: subject.teaching_structure.title,
              });
              model.teaching_structure_id = subject.teaching_structure.id;
            }}
          >
            {subject.teaching_structure.title}
          </div>
        );
      })
    ) : (
      <div>{t('quiz:NoSubject')}</div>
    );
  }
  return (
    <div className="new-quiz-form">
      <div className="flex gap-1">
        <div className="form-group">
          <label className="form-label label-required" htmlFor="name">
            {t('quiz:QuizName')}
            <span>*</span>
          </label>
          <input
            className="form-input"
            type="text"
            name="name"
            placeholder={t('quiz:QuizNamePlaceholder')}
            value={model.title}
            onChange={e => {
              setModelProp(e.target.value, 'title');
            }}
          />
          <Errors errors={validation} prop={'model.title'} />
        </div>

        <div className="form-group">
          <label className="form-label label-required" htmlFor="subject">
            {t('quiz:QuizSubject')}
            <span>*</span>
          </label>
          <div className="dropdown-input-container">
            <ClassDropDown
              dropdownButtonStyle={{ width: '100%', height: '40px' }}
              styleDropDown={{ width: '100%' }}
              value={selectedSubject.title}
              icon={''}
            >
              <div>{printSubjects()}</div>
            </ClassDropDown>

            <Errors errors={validation} prop={'model.teaching_structure_id'} />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="name">
          {t('quiz:QuizDescription')}
        </label>
        <textarea
          placeholder={t('quiz:QuizDescriptionPlaceholder')}
          name="description"
          value={model.description}
          onChange={e => {
            setModelProp(e.target.value, 'description');
          }}
        />
        <Errors errors={validation} prop={'model.description'} />
      </div>

      <div className="form-group form-horizontal">
        <div className="form-group">
          <label className="form-label" htmlFor="passPercentage">
            {t('quiz:QuizProcentage')}
          </label>
          <input
            className="form-input"
            type="number"
            name="passPercentage"
            placeholder="Enter pass percentage"
            value={model.passPercentage}
            onChange={event => {
              const passPercentage = parseInt(event.target.value, 10);

              if (passPercentage < 0 || passPercentage > 100) {
                return;
              }

              setModelProp(passPercentage, 'passPercentage');
            }}
          />
          <Errors errors={validation} prop={'model.passPercentage'} />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="attempts">
            {t('quiz:Attempts')}
          </label>
          <select
            className="form-select"
            name="attempts"
            value={model.attempts}
            onChange={e => {
              const attempts = parseInt(e.target.value, 10);

              if (!attempts && attempts !== 0) {
                setModelProp(undefined, 'attempts');

                return;
              }

              setModelProp(attempts, 'attempts');
            }}
          >
            <option className="form-option" value="">
              &infin;
            </option>

            {quizFormAttempts.map(attempt => (
              <option key={attempt} className="form-option" value={attempt}>
                {attempt}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group-vertical-divider" />

        <div className="form-group duration-option-wrapper">
          <button
            className="duration-option-button"
            type="button"
            disabled={!model.timeControl}
            onClick={() => {
              if (!model.timeControl) {
                return;
              }

              if (model.duration || model.duration === 0) {
                setModelProp(model.duration + 1, 'duration');
              }
            }}
          >
            <PlusIcon />
          </button>

          <div className="form-group">
            <label className="form-label" htmlFor="duration">
              {t('quiz:Duration')}
            </label>
            <input
              className="form-input"
              type="text"
              name="duration"
              placeholder="Enter duration"
              value={`${model.duration} mins`}
              readOnly
              disabled
            />
          </div>

          <button
            className="duration-option-button"
            type="button"
            disabled={!model.timeControl || model.duration === 1}
            onClick={() => {
              if (!model.timeControl || model.duration === 1) {
                return;
              }

              if (model.duration || model.duration === 0) {
                setModelProp(model.duration - 1, 'duration');
              }
            }}
          >
            <MinusIcon />
          </button>
        </div>

        <div className="form-group checkbox-group">
          <input
            className="form-input-checkbox"
            type="checkbox"
            name="timeControl"
            id="timeControl"
            checked={model.timeControl}
            onChange={e => setModelProp(e.target.checked, 'timeControl')}
          />

          <label className="form-label" htmlFor="timeControl">
            {t('quiz:TimeControl')}
          </label>
        </div>
      </div>
      {children}
      <div className="button-wrapper">
        <Button onClick={() => save()} label={t('common:Submit')} color="primary" />
        {editQuiz?.id && (
          <Button
            onClick={() => history.push(quizRoutes?.defaultPath)}
            label={t('common:cancel')}
            color="decline"
          />
        )}
      </div>
    </div>
  );
};

export default CreateQuizForm;
